import { FunctionComponent, useCallback, useState } from 'react'
import clsx from 'clsx'
import { WidgetProps } from '@rjsf/core'
import { useInputLabelDisplay } from '@dxp-module/common'
import {
  Box,
  TextField,
  InputLabel,
  Typography,
  InputAdornment,
  ClickAwayListener,
  IconButton,
} from '@mui/material'
import CalendarIcon from '@mui/icons-material/CalendarToday'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { formatISO } from 'date-fns'
import WithErrorMessage from '../withErrorMessage/WithErrorMessage'
import datePickerStyles from './datePicker.styles'

const DatePickerWidget: FunctionComponent<WidgetProps> = props => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { id, value, onChange, rawErrors, label, required } = props

  const displayedLabel = useInputLabelDisplay(label)

  const styles = datePickerStyles()

  const handleChange = useCallback(
    (newValue: Date | null, keyboardValue?: string) => {
      if (newValue && !keyboardValue) {
        onChange(formatISO(newValue).replace(/T(.*)/g, ''))
      }
    },
    [onChange, value]
  )

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const togglePicker = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const hasErrors = rawErrors?.length > 0

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ClickAwayListener onClickAway={handleClose}>
        <Box className={styles.container}>
          <WithErrorMessage
            error={hasErrors ? rawErrors[0] : undefined}
            className={styles.helper}
            data-test={`${id}:datepicker:error`}
          >
            <Typography variant={'subtitle1'}>
              <InputLabel
                htmlFor={id}
                required={required}
                className={styles.inputLabel}
              >
                {displayedLabel}
              </InputLabel>
            </Typography>
            <DatePicker
              open={isOpen}
              value={value ? new Date(value) : ''}
              onChange={handleChange}
              disableFuture={true}
              onClose={handleClose}
              onAccept={handleChange}
              maxDate={new Date()}
              inputFormat={'dd/MM/yyyy'}
              InputProps={{
                classes: {
                  root: styles.root,
                  input: styles.input,
                  notchedOutline: styles.noBorderRadius,
                },
              }}
              OpenPickerButtonProps={{
                classes: {
                  root: styles.iconButton,
                },
              }}
              PopperProps={{
                className: clsx([
                  {
                    [styles.pickerRoot]: isOpen,
                  },
                ]),
              }}
              /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
              renderInput={({
                classes,
                className,
                InputProps,
                inputRef,
                inputProps,
                ...rest
              }) => (
                <TextField
                  id={id}
                  className={className}
                  fullWidth
                  inputRef={inputRef}
                  error={hasErrors}
                  required={required}
                  onClick={togglePicker}
                  value={value}
                  classes={{
                    root: clsx([
                      classes?.root,
                      {
                        [styles.error]: hasErrors,
                      },
                    ]),
                  }}
                  inputProps={{
                    ...inputProps,
                    readOnly: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        className={styles.iconButton}
                        position="end"
                      >
                        <IconButton onClick={handleOpen}>
                          <CalendarIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    ...InputProps,
                  }}
                  {...rest}
                />
              )}
            />
          </WithErrorMessage>
        </Box>
      </ClickAwayListener>
    </LocalizationProvider>
  )
}

export default DatePickerWidget
